import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import { IApiError } from "src/library/utils/errors/apiError";
import { Button } from "src/library/components/Button";
import {
  CascadeSetting,
  FormRow
} from "src/library/components/Forms";
import Grid from "src/library/components/Hedron";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import useCascadeLeadTimeSettings, {
  cascadeSettingsSchema,
  IFormFieldValues
} from "src/core/shared/components/useCascadeLeadTimeSettings";
import { useInheritedSettingsApi } from "src/core/shared/api/inheritedSettings.api";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import { ERPType } from "src/core/shared/api/globalSettings.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { UpdatedSettingsFields } from "src/library/components/Forms/CascadeSettingField";
import {
  IInheritedSetting,
  InheritedSettingNameType,
  OrderedInheritedSettings
} from "src/core/drops/api/drops.api";

interface UsersPageProps {
  customerId: string;
}

/**
 * Customer lead times settings page
 *
 */
const CustomerLeadTimesPage: React.ReactFCWithChildren<PageProps<unknown, UsersPageProps>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const { customer } = useCustomer(customerId);
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ getSettingsResponse, getSettings ] = useInheritedSettingsApi("GET_CUSTOMER_SETTINGS");
  const [ inheritedValues, setInheritedValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ overrideValues, setOverrideValues ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const formRef = React.useRef<Formik<IFormFieldValues>>(null);
  const [ requestsCount, setRequestsCount ] = React.useState<number | null>(null);
  const [ errors, setErrors ] = React.useState<IApiError[]>([]);
  const checkPermission = useCheckUserPermission();
  const [ initialValues ] = useCascadeLeadTimeSettings(terminology.customer, inheritedValues, overrideValues);
  const [ settingsToUpdate, setSettingsToUpdate ] = React.useState<UpdatedSettingsFields>([]);
  const [ isSubmitting, setIsSubmitting ] = React.useState(false);

  const canAmend = React.useMemo(() => {
    return checkPermission("amend-global-settings");
  }, [ checkPermission ]);

  React.useEffect(() => {
    getSettings({ customerId });
  }, [ customerId, getSettings ]);

  React.useEffect(() => {
    if (getSettingsResponse.data) {
      const inheritedToSet: OrderedInheritedSettings<IInheritedSetting> = {};
      const overridesToSet: OrderedInheritedSettings<IInheritedSetting> = {};

      _.each(getSettingsResponse.data, lts => {
        const globalSetting = _.find(lts, setting => setting.scope === "global");
        const customerSetting = _.find(lts, setting => setting.scope === "customer");

        if (customerSetting) {
          overridesToSet[ customerSetting.name ] = customerSetting;
        }

        if (customerSetting) {
          inheritedToSet[ customerSetting.name ] = customerSetting;
        } else if (globalSetting) {
          inheritedToSet[ globalSetting.name ] = globalSetting;
        }
      });
      setOverrideValues(overridesToSet);
      setInheritedValues(inheritedToSet);
      getSettingsResponse.data = null;
    }

    if (getSettingsResponse.error) {
      setGlobalError(getSettingsResponse.error);
    }
  }, [ getSettingsResponse ]);

  React.useEffect(() => {
    if (requestsCount === 0) {
      // setSettings(null);

      if (errors.length === 0) {
        toast.success("Settings saved!");
      } else {
        if (errors.length === 1) {
          toast.error(`Could not save settings: ${errors[ 0 ].message}. Please try again.`);
        } else {
          toast.error(errors[ 0 ].message);
        }

        setErrors([]);
      }

      getSettings({ customerId });
    }
  }, [
    requestsCount,
    errors,
    getSettings,
    customerId
  ]);

  // This preps what settings need to be updated and saves them in settingsToUpdate list
  // The list is then handled by each CascadeSetting component which actions the update
  const handleSubmitSettings = (form: IFormFieldValues) => {
    const requests: UpdatedSettingsFields = [];

    _.each(form, setting => {
      if (setting) {
        const initialValue: IFormFieldValues["delivery_cutoff"] | IFormFieldValues["delivery_week_leadtimes"] | null = (initialValues && initialValues[ setting.name ]) || null;
        const originalOverride = (overrideValues && overrideValues[ setting.name ]) || null;

        if (originalOverride) {
          if (setting.inherited) {
            // original override found
            requests.push({
              params: {
                name: setting.name,
                id: originalOverride.id
              },
              action: "delete"
            });
          } else {
            const params = {
              ...originalOverride,
              value: setting.value
            };

            // Only push update if value has changed
            if (setting.value !== initialValue?.value) {
              requests.push({
                params,
                action: "update"
              });
            }
          }
        } else {
          // no original override
          if (!setting.inherited) {
            const params: Omit<IInheritedSetting, "id"> = {
              scope: "customer",
              value: setting.value,
              entityId: customerId,
              name: setting.name
            };

            requests.push({
              params,
              action: "create"
            });
          }
        }
      }
    });
    setRequestsCount(requests.length);
    setSettingsToUpdate(requests);
  };

  React.useEffect(() => {
    if (requestsCount === 0 && !getSettingsResponse.pending) {
      if (formRef.current && initialValues) {
        formRef.current.setValues(initialValues);
      }
    }
  }, [
    getSettingsResponse.pending,
    initialValues,
    requestsCount
  ]);

  const formatErrors = (errors: any, settingName: InheritedSettingNameType) => {
    if (errors) {
      if (errors[ settingName ]) {
        if (typeof errors[ settingName ].value === "object") {
          // formats errors for the message based on settings key in the error
          const result = Object.keys(errors[ settingName ].value).reduce((r, k) => {
            if (typeof errors[ settingName ].value[ k ] === "object") {
              return Object.keys(errors[ settingName ].value[ k ]).reduce((_n, p) => {
                return r.concat(errors[ settingName ].value[ k ][ p ]);
              }, []);
            }

            return r.concat(errors[ settingName ].value[ k ]);
          }, []);

          return `${result}`;
        }

        return `${errors[ settingName ].value}`;
      }
    }

    return null;
  };

  return (
    <Layout
      {...props}
      title={customer ? `${customer.name} - Defaults & Limits` : ""}
    >
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {initialValues && (
            <Formik
              ref={formRef}
              initialValues={initialValues}
              validationSchema={cascadeSettingsSchema()}
              validateOnChange
              validateOnBlur
              onSubmit={handleSubmitSettings}
              render={({
                handleSubmit, handleBlur, values, setFieldValue, errors
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    {_.map(values, setting => {
                      if (setting) {
                        if (setting.hidden) {
                          return null;
                        }

                        return (
                          <CascadeSettingWrapper
                            id={`${setting.name}`}
                            key={setting.name}
                          >
                            {setting.Description}

                            {errors[ setting.name ] && (
                              <InlineError>
                                {formatErrors(errors, setting.name)}
                              </InlineError>
                            )}

                            <CascadeSetting
                              erp={erp as ERPType}
                              name={setting.name}
                              label={setting.label}
                              defaultChecked={setting.inherited}
                              disabled={!canAmend}
                              setDefaultChecked={defaultChecked => {
                                if (defaultChecked) {
                                  setFieldValue(setting.name, {
                                    ...setting,
                                    inherited: true,
                                    value: setting.inheritedValue
                                  });
                                } else {
                                  setFieldValue(setting.name, {
                                    ...setting,
                                    inherited: false
                                  });
                                }
                              }}
                              SettingElement={setting.SettingElement(setFieldValue, setting, handleBlur, !canAmend, values)}
                              // Props to manage saving updates to settings
                              settingsToUpdate={settingsToUpdate}
                              updateSettingsToUpdate={setSettingsToUpdate}
                              updateIsSubmiting={setIsSubmitting}
                              updateRequestsCount={setRequestsCount}
                              updateErrors={setErrors}
                            />
                          </CascadeSettingWrapper>
                        );
                      }

                      return null;
                    })}

                    <FormRow>
                      <Button
                        onClick={() => formRef.current?.submitForm()}
                        loading={isSubmitting}
                      >
                        Save
                      </Button>
                    </FormRow>
                  </form>
                );
              }}
            />
          )}
        </Grid.Bounds>
      </MainContentWrapper>

    </Layout>
  );
};

const InlineError = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.error};
    padding: ${theme.spacing.padding.sm};
    border-radius: ${theme.borderRadius};
  `}
  color: white;
`;

const CascadeSettingWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.padding.lg};
`;

export default CustomerLeadTimesPage;
